import moment from 'moment';
import { LaRochelleFutureFacilitiesLegendIcon, LAVDedicatedRoadsLegendIcon, LAVSharedRoadsLegendIcon, LAVShuttlesLegendIcon } from '../components';
import { defaultEnvironment } from '../environment';
import { dashboardTabsStatus, Partner, PartnerContract, PartnerContractTemplate, PartnerGroupMemberWithoutEmployee, PartnerUser, Permissions, Place } from '../models';
import { administrativeLevels, backendAdministrativeLevels } from './cyclability-zone';
import { parseCertificate, parseEmployee } from './employee';
import { GeocoderService } from './geocoder';
import { HttpService } from './http';
import { backendBikeReasonsMap, backendDemotivationReasonsMap, backendMobilitySurveyTransportModes, bikeReasonsMap, demotivationReasonsMap, parseMobilitySurveyStats, transportModesMap } from './mobility-survey';
import { UserService } from './user';
export const emptyTransportHabitsFilters = {
  transportModes: [],
  vaeTiers: [],
  demotivationReasons: [],
  bikeReasons: [],
  activityTags: [],
  conversionProfiles: []
};
export class PartnerService {
  static async getPartners({
    isAdmin,
    orderBy,
    page,
    pageSize,
    query,
    search,
    contractTemplateCodes,
    contractTemplateStates,
    geogroupId
  }) {
    try {
      if (!UserService.currentUser?.isGeovelo) throw new Error('user not allowed');
      const queryParams = [{
        key: 'page',
        value: page
      }];
      if (orderBy) queryParams.push({
        key: 'ordering',
        value: orderBy
      });
      if (pageSize) queryParams.push({
        key: 'page_size',
        value: pageSize
      });
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      if (search) queryParams.push({
        key: 'search',
        value: search
      });
      if (geogroupId) queryParams.push({
        key: 'geo_group',
        value: geogroupId
      });
      if (contractTemplateCodes) queryParams.push({
        key: 'contract_template_codes',
        value: contractTemplateCodes.join(',')
      });
      if (contractTemplateStates) queryParams.push({
        key: 'state[]',
        value: contractTemplateStates.join(',')
      });
      const {
        results,
        count,
        next
      } = await HttpService.get('v3', `/partners`, queryParams);
      return {
        count,
        next,
        partners: results.reduce((res, props) => {
          const partner = parsePartner({
            partner: props
          }, isAdmin);
          if (partner) res.push(partner);
          return res;
        }, [])
      };
    } catch (err) {
      console.error('[PartnerService][getPartners]', err);
      throw err;
    }
  }
  static async getPremiumPartners({
    page,
    pageSize,
    search,
    contractTemplateCodes,
    contractTemplateStates
  }) {
    try {
      const queryParams = [{
        key: 'page',
        value: page
      }];
      if (pageSize) queryParams.push({
        key: 'page_size',
        value: pageSize
      });
      if (search) queryParams.push({
        key: 'search',
        value: search
      });
      if (contractTemplateCodes) queryParams.push({
        key: 'contract_template_codes',
        value: contractTemplateCodes.join(',')
      });
      if (contractTemplateStates) queryParams.push({
        key: 'state[]',
        value: contractTemplateStates.join(',')
      });
      const {
        results,
        count,
        next
      } = await HttpService.get('v3', `/partners/premium_partners`, queryParams);
      return {
        count,
        next,
        partners: results.map(({
          id,
          geo_group: geogroupId,
          enterprise_site_count: sitesCount
        }) => ({
          id,
          geogroupId,
          sitesCount
        }))
      };
    } catch (err) {
      console.error('[PartnerService][getPremiumPartners]', err);
      throw err;
    }
  }
  static async getPartner({
    isAdmin,
    partner: {
      id,
      code,
      query
    }
  }) {
    try {
      const queryParams = [];
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      const data = await HttpService.get('v3', `/partners/${id}`, queryParams, [], null, {
        partner: code
      });
      const partner = parsePartner({
        is_admin_partner: isAdmin,
        partner: data
      });
      if (!partner) throw new Error('Partner not found');
      return partner;
    } catch (err) {
      console.error('[PartnerService][getPartner]', err);
      throw err;
    }
  }
  static async addPartner({
    title,
    administrativeLevel,
    areaId,
    geoagglo,
    geogroup,
    cyclabilityZoneId,
    icon,
    contracts,
    contactEmail,
    whiteListedDomains,
    siren,
    siret,
    creationReasons
  }) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const props = {
        title,
        geo_agglo: geoagglo?.id || null,
        geo_group: geogroup?.id || null,
        cyclability_zone: cyclabilityZoneId || null,
        area: areaId || geogroup?.areaId || null,
        contact_email: contactEmail || null,
        contracts: (contracts || []).map(({
          startDate,
          endDate,
          apiUsageLimit,
          contractTemplateId
        }) => ({
          title,
          start_datetime: startDate,
          end_datetime: endDate,
          api_usage_limit: apiUsageLimit,
          contract_template: contractTemplateId
        }))
      };
      if (administrativeLevel && administrativeLevel !== 'world') {
        props.administrative_level = backendAdministrativeLevels[administrativeLevel];
      }
      if (whiteListedDomains) props.whitelisted_domains = whiteListedDomains;
      if (siren) props.siren_number = siren;
      if (siret) props.siret_number = siret;
      if (siren || siret) props.siren_source = 'ADMINISTRATOR';
      if (creationReasons) props.creation_reason = creationReasons;
      let body;
      if (icon) {
        body = new FormData();
        body.append('icon', icon);
        body.append('data', JSON.stringify(props));
      } else {
        body = JSON.stringify(props);
      }
      const data = await HttpService.post('v3', `/partners`, [], [], body, {
        expectedStatus: 201
      });
      const partner = parsePartner({
        is_admin_partner: true,
        partner: data
      });
      if (!partner) throw new Error('invalid partner');
      return partner;
    } catch (err) {
      console.error('[PartnerService][addPartner]', err);
      throw err;
    }
  }
  static async editPartner(partner, {
    title,
    contactEmail,
    icon,
    whiteListedDomains,
    administrativeLevel,
    areaId,
    geoagglo,
    geogroup,
    cyclabilityZoneId,
    creationReasons,
    siren,
    siret
  }, isAdmin) {
    try {
      if (!UserService.currentUser) throw new Error('user not connected');
      const props = {};
      if (title !== undefined) props.title = title;
      if (contactEmail !== undefined) props.contact_email = contactEmail;
      if (whiteListedDomains) props.whitelisted_domains = whiteListedDomains;
      if (icon === null) props.icon = null;
      if (geoagglo !== undefined) props.geo_agglo = geoagglo?.id || null;
      if (geogroup !== undefined) props.geo_group = geogroup?.id || null;
      if (areaId !== undefined) props.area = areaId;
      if (cyclabilityZoneId !== undefined) props.cyclability_zone = cyclabilityZoneId;
      if (administrativeLevel !== undefined && administrativeLevel !== 'world') {
        props.administrative_level = administrativeLevel === null ? null : backendAdministrativeLevels[administrativeLevel];
      }
      if (creationReasons) props.creation_reason = creationReasons;
      if (siren) props.siren_number = siren;
      if (siret) props.siret_number = siret;
      if (siren || siret) props.siren_source = 'ADMINISTRATOR';
      let body;
      if (icon) {
        body = new FormData();
        body.append('icon', icon);
        body.append('data', JSON.stringify(props));
      } else {
        body = JSON.stringify(props);
      }
      const data = await HttpService.patch('v3', `/partners/${partner.id}`, [], [], body, {
        partner: partner.code,
        expectedStatus: 200
      });
      const updatedPartner = parsePartner({
        partner: data
      }, isAdmin);
      if (!updatedPartner) throw new Error('');
      return updatedPartner;
    } catch (err) {
      console.error('[PartnerService][editPartner]', err);
      throw err;
    }
  }
  static async removePartner(partner) {
    try {
      await HttpService.delete('v3', `/partners/${partner.id}`, [], [], null, partner.code ? {
        partner: partner.code
      } : undefined);
      return true;
    } catch (err) {
      console.error('[PartnerService][removePartner]', err);
      throw err;
    }
  }
  static async getLayers() {
    try {
      const data = await HttpService.get('v2', '/partners/layers');
      return data.reduce((res, _data) => {
        const layer = parsePartnerLayer(_data);
        if (layer) res.push(layer);
        return res;
      }, []);
    } catch (err) {
      console.error('[PartnerService][getLayers]', err);
      throw err;
    }
  }
  static async getPartnerUsers(id, {
    page,
    rowsPerPage
  }) {
    try {
      const {
        count,
        next,
        previous,
        results
      } = await HttpService.get('v3', `/partners/${id}/users`, [{
        key: 'ordering',
        value: '-id'
      }, {
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: rowsPerPage
      }]);
      const users = results.map(parsePartnerUser);
      return {
        count,
        next,
        previous,
        users
      };
    } catch (err) {
      console.error('[PartnerService][getPartnerUsers]', err);
      throw err;
    }
  }
  static async removePartnerUser(partner, userId) {
    try {
      await HttpService.delete('v3', `/partners/${partner.id}/users/${userId}`, [], [], null, {
        partner: partner.code
      });
      return true;
    } catch (err) {
      console.error('[PartnerService][removePartnerUser]', err);
      throw err;
    }
  }
  static async addPartnerUser(partner, userId, is_admin) {
    try {
      const data = await HttpService.post('v3', `/partners/${partner.id}/users`, [], [], JSON.stringify({
        user: userId,
        is_admin_partner: !!is_admin
      }), {
        partner: partner.code
      });
      return data.user;
    } catch (err) {
      console.error('[PartnerService][addPartnerUser]', err);
      throw err;
    }
  }
  static async updatePartnerUser(partner, userId, is_admin) {
    const {
      id: partnerId,
      code
    } = partner;
    try {
      const data = await HttpService.patch('v3', `/partners/${partnerId}/users/${userId}`, [], [], JSON.stringify({
        user: userId,
        is_admin_partner: is_admin
      }), {
        partner: code,
        expectedStatus: 200
      });
      return data.user;
    } catch (err) {
      if (typeof err === 'object' && err && 'status' in err && err.status === 404) {
        return PartnerService.addPartnerUser(partner, userId, is_admin);
      } else {
        console.error('[PartnerService][updatePartnerUser]', err);
        throw err;
      }
    }
  }
  static async getContractTemplates({
    query
  } = {}) {
    try {
      const params = [];
      if (query) params.push({
        key: 'query',
        value: query
      });
      const res = await HttpService.get('v3', `/partner_contract_templates`, params);
      return res.map(parsePartnerContractTemplate).sort((a, b) => a.title?.localeCompare(b.title));
    } catch (err) {
      console.error('[PartnerService][getContractTemplates]', err);
      throw err;
    }
  }
  static sortContracts(a, b) {
    if (a.endDateTime && b.endDateTime) return a.endDateTime.isSame(b.endDateTime) ? a.title.localeCompare(b.title) : a.endDateTime.isBefore(b.endDateTime) ? 1 : -1;
    if (a.endDateTime) return 1;
    if (b.endDateTime) return -1;
    return a.title.localeCompare(b.title);
  }
  static async getContracts(partnerId, {
    query
  } = {}) {
    try {
      const queryParams = [];
      if (query) queryParams.push({
        key: 'query',
        value: query
      });
      const res = await HttpService.get('v3', `/partners/${partnerId}/contracts`, queryParams);
      return res.map(parsePartnerContract).sort(this.sortContracts);
    } catch (err) {
      console.error('[PartnerService][getContracts]', err);
      throw err;
    }
  }
  static async addContract({
    partner,
    title,
    startDate,
    endDate,
    apiUsageLimit,
    contractTemplateId,
    companyMaxSize
  }) {
    try {
      const params = {
        title,
        start_datetime: startDate,
        end_datetime: endDate,
        api_usage_limit: apiUsageLimit,
        contract_template: contractTemplateId
      };
      if (companyMaxSize !== undefined) params.company_workforce_count_limit = companyMaxSize;
      const res = await HttpService.post('v3', `/partners/${partner.id}/contracts`, [], [], JSON.stringify(params), {
        partner: partner.code
      });
      return parsePartnerContract(res);
    } catch (err) {
      console.error('[PartnerService][addContract]', err);
      throw err;
    }
  }
  static async updateContract({
    partner,
    contractId,
    title,
    startDate,
    endDate,
    apiUsageLimit,
    contractTemplateId,
    companyMaxSize
  }) {
    try {
      const params = {
        title,
        contract_template: contractTemplateId
      };
      if (startDate) params.start_datetime = startDate;
      if (endDate) params.end_datetime = endDate;
      if (apiUsageLimit !== undefined) params.api_usage_limit = apiUsageLimit;
      if (companyMaxSize !== undefined) params.company_workforce_count_limit = companyMaxSize;
      const res = await HttpService.patch('v3', `/contracts/${contractId}`, [], [], JSON.stringify(params), {
        partner: partner.code
      });
      return parsePartnerContract(res);
    } catch (err) {
      console.error('[PartnerService][updateContract]', err);
      throw err;
    }
  }
  static async getCustomerInfos({
    partner
  }) {
    try {
      const data = await HttpService.get('v1', `/partners/${partner.id}/customer_infos`, [], [], null, {
        partner: partner.code,
        expectedStatus: 200
      });
      return parseCustomerInfos(data);
    } catch (err) {
      if (typeof err === 'object' && err && 'status' in err && err.status === 404) return null;
      console.error('[PartnerService][getCustomerInfos]', err);
      throw err;
    }
  }
  static async saveCustomerInfos({
    partner,
    exists,
    companyName,
    companyAddress,
    countryCode,
    contactName,
    contactEmail,
    sirenNumber,
    tvaNumber
  }) {
    try {
      const params = {
        company_name: companyName,
        company_address: companyAddress,
        company_country_code: countryCode,
        contact_name: contactName,
        contact_email: contactEmail,
        siren_number: sirenNumber,
        tva_number: tvaNumber
      };
      const data = exists ? await HttpService.put('v1', `/partners/${partner.id}/customer_infos`, [], [], JSON.stringify(params), {
        partner: partner.code,
        expectedStatus: 200
      }) : await HttpService.post('v1', `/partners/${partner.id}/customer_infos`, [], [], JSON.stringify(params), {
        partner: partner.code,
        expectedStatus: 201
      });
      return parseCustomerInfos(data);
    } catch (err) {
      console.error('[PartnerService][saveCustomerInfos]', err);
      throw err;
    }
  }
  static async deleteContract(contractId, {
    partner
  }) {
    try {
      await HttpService.delete('v3', `/contracts/${contractId}`, [], [], null, {
        partner: partner.code
      });
      return true;
    } catch (err) {
      console.error('[PartnerService][deleteContract]', err);
      throw err;
    }
  }
  static async getFlowsStats(partnerId, {
    period
  }) {
    try {
      const stats = await HttpService.get('v1', `/stats/partner/${partnerId}/flow_stats`, [{
        key: 'startDate',
        value: period.from.format('YYYY-MM-DD')
      }, {
        key: 'endDate',
        value: period.from.clone().add(1, 'month').format('YYYY-MM-DD')
      }], [], null, {
        expectedStatus: 200
      });
      return parsePartnerFlowsStats(stats);
    } catch (err) {
      if (typeof err === 'object' && err && 'status' in err && err.status === 404) return null;
      console.error('[PartnerService][getFlowsStats]', err);
      throw err;
    }
  }
  static async getH3Frequencies({
    id: partnerId,
    code: partner
  }, {
    period
  }) {
    try {
      const results = await HttpService.get('v1', `/stats/partner/${partnerId}/h3_frequencies`, [{
        key: 'startDate',
        value: period.from.format('YYYY-MM-DD')
      }, {
        key: 'endDate',
        value: period.to.format('YYYY-MM-DD')
      }], [], null, {
        partner
      });
      return results.map(({
        h3_index: h3Index,
        frequency
      }) => ({
        h3Index,
        frequency
      }));
    } catch (err) {
      console.error('[PartnerService][getH3Frequencies]', err);
      throw err;
    }
  }
  static async initializeCompany({
    id,
    code
  }) {
    try {
      const data = await HttpService.get('v1', `/partners/${id}/initialize_enterprise`, [], [], null, {
        partner: code
      });
      const partner = parsePartner({
        partner: data
      });
      if (!partner) throw new Error('partner not found');
      return partner;
    } catch (err) {
      console.error('[PartnerService][initializeCompany]', err);
      throw err;
    }
  }
  static async getEmployees(partner, {
    onlyAdmins,
    roles,
    onboardingStatus,
    linkStatus,
    groupsIds,
    search,
    statuses,
    onlyFMDBeneficiaries,
    ordering,
    page,
    pageSize,
    query
  }) {
    try {
      const params = PartnerService.getPartnerEmployeesParams({
        groupsIds,
        linkStatus,
        onlyAdmins,
        onlyFMDBeneficiaries,
        onboardingStatus,
        ordering,
        page,
        pageSize,
        query,
        roles,
        search,
        statuses
      });
      const {
        count,
        next,
        results
      } = await HttpService.get('v1', `/partners/${partner.id}/employees`, params, [], null, {
        partner: partner.code
      });
      return {
        count,
        hasNext: Boolean(next),
        employees: results.map(parseEmployee)
      };
    } catch (err) {
      console.error('[PartnerService][getEmployees]', err);
      throw err;
    }
  }
  static async getGroupMembersWithoutEmployees(partner) {
    try {
      const results = await HttpService.get('v1', `/partners/${partner.id}/group_members_without_employees`, [], [], null, {
        partner: partner.code
      });
      return results.map(parsePartnerGroupMemberWithoutEmployee);
    } catch (err) {
      console.error('[PartnerService][getGroupMembersWithoutEmployees]', err);
      throw err;
    }
  }
  static async getHoldingEmployees(partner, {
    onlyAdmins,
    roles,
    onboardingStatus,
    linkStatus,
    groupsIds,
    search,
    statuses,
    onlyFMDBeneficiaries,
    ordering,
    page,
    pageSize,
    query
  }) {
    try {
      const params = PartnerService.getPartnerEmployeesParams({
        groupsIds,
        linkStatus,
        onlyAdmins,
        onlyFMDBeneficiaries,
        onboardingStatus,
        ordering,
        page,
        pageSize,
        query,
        roles,
        search,
        statuses
      });
      const {
        count,
        next,
        results
      } = await HttpService.get('v1', `/holding/partners/${partner.id}/employees`, params, [], null, {
        partner: partner.code
      });
      return {
        count,
        hasNext: Boolean(next),
        employees: results.map(parseEmployee)
      };
    } catch (err) {
      console.error('[PartnerService][getHoldingEmployees]', err);
      throw err;
    }
  }
  static getPartnerEmployeesParams({
    onlyAdmins,
    roles,
    onboardingStatus,
    linkStatus,
    groupsIds,
    search,
    statuses,
    onlyFMDBeneficiaries,
    ordering,
    page,
    pageSize,
    query
  }) {
    const params = [{
      key: 'ordering',
      value: ordering
    }, {
      key: 'page',
      value: page
    }, {
      key: 'page_size',
      value: pageSize
    }, {
      key: 'query',
      value: query
    }];
    if (onlyAdmins !== undefined) {
      params.push({
        key: 'only_admins',
        value: onlyAdmins
      });
    }
    if (roles !== undefined) {
      params.push(...roles.map(key => ({
        key: 'role[]',
        value: key
      })));
    }
    if (onboardingStatus !== undefined) {
      params.push({
        key: 'filter_employees_with_completed_onboarding',
        value: onboardingStatus === 'completed' ? true : false
      });
    }
    if (linkStatus === 'linked') params.push({
      key: 'filter_employees_link_with_a_user',
      value: true
    });
    if (groupsIds) {
      params.push(...groupsIds.map(id => ({
        key: 'group_ids[]',
        value: id
      })));
    }
    if (search) params.push({
      key: 'search',
      value: search
    });
    if (statuses && statuses.length > 0) {
      const statusesMap = {
        joined: 'EMPLOYEE_JOIN',
        left: 'EMPLOYEE_QUIT',
        waitingForApproval: 'EMPLOYEE_WAITING_FOR_APPROVAL',
        waitingUserToJoin: 'WAITING_USER_TO_JOIN'
      };
      statuses.forEach(key => params.push({
        key: 'join_status[]',
        value: statusesMap[key]
      }));
    }
    if (onlyFMDBeneficiaries !== undefined) {
      params.push({
        key: 'only_employee_can_declare_fmd',
        value: onlyFMDBeneficiaries
      });
    }
    return params;
  }
  static async importEmployees(partner, {
    csv
  }) {
    try {
      const formData = new FormData();
      formData.append('csv', csv);
      formData.append('delimiter', ',');
      const res = await HttpService.post('v1', `/partners/${partner.id}/import_employees`, [], [], formData, {
        partner: partner.code
      });
      return res.length;
    } catch (err) {
      console.error('[PartnerService][importEmployees]', err);
      throw err;
    }
  }
  static async inviteEmployeesAgain(partner, {
    employees
  }) {
    try {
      await HttpService.post('v1', `/partners/${partner.id}/re_invite_employees`, [], [], JSON.stringify({
        employee_ids: employees.map(({
          id
        }) => id)
      }), {
        partner: partner.code
      });
      return true;
    } catch (err) {
      console.error('[PartnerService][inviteEmployeesAgain]', err);
      throw err;
    }
  }
  static async acceptEmployees({
    partner,
    geogroup,
    employees
  }) {
    try {
      await HttpService.post('v2', `/geogroups/${geogroup.id}/join_approvals`, [], [], JSON.stringify({
        employee_ids: employees.map(({
          id
        }) => id)
      }), {
        partner: partner.code
      });
      return true;
    } catch (err) {
      console.error('[PartnerService][acceptEmployees]', err);
      throw err;
    }
  }
  static async rejectEmployees({
    partner,
    geogroup,
    employees
  }) {
    try {
      await HttpService.post('v2', `/geogroups/${geogroup.id}/join_disapprovals`, [], [], JSON.stringify({
        employee_ids: employees.map(({
          id
        }) => id)
      }), {
        partner: partner.code
      });
      return true;
    } catch (err) {
      console.error('[PartnerService][acceptEmployees]', err);
      throw err;
    }
  }
  static async getCertificates({
    partner,
    page,
    pageSize,
    startDate,
    endDate,
    employeeIds,
    groupsIds,
    onlyValid,
    onlyInvalid,
    onlyBeneficiaries,
    search,
    ordering,
    query
  }) {
    try {
      const params = [{
        key: 'page',
        value: page
      }, {
        key: 'page_size',
        value: pageSize
      }, {
        key: 'period',
        value: 'custom'
      }, {
        key: 'date_start',
        value: startDate.format('YYYY-MM-DD')
      }, {
        key: 'date_end',
        value: endDate.format('YYYY-MM-DD')
      }, {
        key: 'exclude_null_amount_to_pay',
        value: false
      }];
      if (employeeIds && employeeIds.length > 0) params.push({
        key: 'employee_id[]',
        value: employeeIds.join(',')
      });
      if (groupsIds && groupsIds.length > 0) params.push(...groupsIds.map(id => ({
        key: 'sub_group_id[]',
        value: id
      })));
      if (onlyValid) params.push({
        key: 'valid_attestation',
        value: true
      }, {
        key: 'exclude_temporary_attestation',
        value: true
      });else if (onlyInvalid) params.push({
        key: 'invalid_or_temporary_attestation',
        value: true
      });
      if (onlyBeneficiaries !== undefined) params.push({
        key: 'can_declare_fmd',
        value: onlyBeneficiaries
      });
      if (search) params.push({
        key: 'search',
        value: search
      });
      if (ordering) params.push({
        key: 'ordering',
        value: ordering
      });
      if (query) params.push({
        key: 'query',
        value: query
      });
      const {
        count,
        results
      } = await HttpService.get('v2', `/partners/${partner.id}/fmd_attestations`, params, [], null, {
        partner: partner.code
      });
      return {
        count,
        items: results.map(({
          fmd_attestations,
          ...data
        }) => ({
          employee: parseEmployee(data),
          certificates: fmd_attestations?.map(parseCertificate) || []
        }))
      };
    } catch (err) {
      console.error('[PartnerService][getCertificates]', err);
      throw err;
    }
  }
  static async invalidateCertificates({
    partner,
    certificatesIds: attestation_ids,
    reason,
    description
  }) {
    try {
      await HttpService.post('v1', `/partners/${partner.id}/invalidate_fmd_attestations`, [], [], JSON.stringify({
        attestation_ids,
        reason: backendCertificateInvalidationReasonsMap[reason],
        ...(description !== null ? {
          description
        } : {})
      }), {
        partner: partner.code
      });
      return true;
    } catch (err) {
      console.error('[PartnerService][invalidateCertificates]', err);
      throw err;
    }
  }
  static async getFMDStats({
    partner,
    startDate,
    endDate,
    disableTemporary
  }) {
    try {
      const queryParams = [{
        key: 'period',
        value: 'custom'
      }, {
        key: 'date_start',
        value: startDate.format('YYYY-MM-DD')
      }, {
        key: 'date_end',
        value: endDate.format('YYYY-MM-DD')
      }];
      if (disableTemporary) {
        queryParams.push({
          key: 'temporary_attestation',
          value: false
        }, {
          key: 'valid_attestation',
          value: true
        });
      }
      const {
        amount_to_pay: allowance,
        count_days_total: daysCount,
        sum_distance_total: totalDistance,
        count_home_to_work_journeys: homeToWorkCount,
        count_work_to_home_journeys: workToHomeCount
      } = await HttpService.get('v1', `/partners/${partner.id}/fmd_attestations_stats`, queryParams, [], null, {
        partner: partner.code
      });
      return {
        allowance: allowance || 0,
        daysCount: daysCount || 0,
        totalDistance: totalDistance || 0,
        homeToWorkCount: homeToWorkCount || 0,
        workToHomeCount: workToHomeCount || 0
      };
    } catch (err) {
      console.error('[PartnerService][getFMDStats]', err);
      throw err;
    }
  }
  static async getHomeWorkDistances({
    partner,
    geogroupId
  }) {
    try {
      const params = [];
      if (geogroupId) params.push({
        key: 'group_id',
        value: geogroupId
      });
      const {
        employee_home_work_distances_meter: distances
      } = await HttpService.get('v1', `/partners/${partner.id}/employee_home_work_distances`, params, [], null, {
        partner: partner.code
      });
      return distances;
    } catch (err) {
      console.error('[PartnerService][getHomeWorkDistances]', err);
      throw err;
    }
  }
  static async searchCompanies({
    search,
    isSite
  }) {
    try {
      const params = [{
        key: 'search',
        value: search
      }];
      if (isSite) params.push({
        key: 'return_matching_etablissements',
        value: true
      });
      const {
        results
      } = await HttpService.get('v1', `/companies`, params);
      if (isSite) {
        if (!results[0]) return [];
        const {
          nom_raison_sociale,
          matching_etablissements
        } = results[0];
        if (!matching_etablissements?.[0]) return [];
        const {
          etat_administratif,
          siret,
          adresse,
          latitude,
          longitude,
          libelle_commune
        } = matching_etablissements[0];
        if (etat_administratif === 'F') return [];
        return [{
          isSite: true,
          siret: siret || '',
          name: `${nom_raison_sociale || ''}${libelle_commune ? ` (${libelle_commune})` : ''}`,
          place: new Place(siret || '', {
            type: 'Point',
            coordinates: [parseFloat(longitude), parseFloat(latitude)]
          }, {
            primaryText: adresse || '',
            city: libelle_commune
          })
        }];
      }
      await Promise.all(results.map(async result => {
        if (result.siege?.adresse && (!result.siege.latitude || !result.siege.longitude)) {
          const [place] = await GeocoderService.autocomplete(result.siege.adresse, {
            nbResults: 1
          });
          if (place) result.place = place;
        }
        return result;
      }));
      return results.reduce((res, {
        siren,
        etat_administratif,
        nom_raison_sociale,
        nombre_etablissements_ouverts,
        siege,
        place
      }) => {
        if (siren && etat_administratif === 'A' && nom_raison_sociale && siege && nombre_etablissements_ouverts && nombre_etablissements_ouverts > 0) {
          const {
            latitude,
            longitude,
            numero_voie,
            type_voie,
            libelle_voie,
            code_postal,
            libelle_commune
          } = siege;
          if (!place && (!longitude || !latitude)) return res;
          res.push({
            siren,
            name: nom_raison_sociale || '',
            place: new Place(siren, place?.point || {
              type: 'Point',
              coordinates: [parseFloat(longitude || ''), parseFloat(latitude || '')]
            }, {
              primaryText: [numero_voie, type_voie, libelle_voie].filter(Boolean).join(' '),
              secondaryText: [code_postal, libelle_commune].filter(Boolean).join(' ')
            }),
            sitesCount: nombre_etablissements_ouverts || 0
          });
        }
        return res;
      }, []);
    } catch (err) {
      console.error('[PartnerService][searchCompanies]', err);
      throw err;
    }
  }
  static async getCompanyEstablishment({
    siren,
    sitesCount
  }) {
    try {
      const nbPages = Math.ceil(sitesCount / 1000);
      const res = (await Promise.all(new Array(nbPages).fill(null).map((_, index) => HttpService.get('v1', `/companies/${siren}/etablisements`, [{
        key: 'page_size',
        value: 1000
      }, {
        key: 'start_item',
        value: 1000 * index
      }])))).flatMap(({
        etablissements
      }) => etablissements);
      return res.reduce((res, {
        siret,
        adresseEtablissement,
        etablissementSiege,
        periodesEtablissement
      }) => {
        if (siret && adresseEtablissement && periodesEtablissement?.find(({
          etatAdministratifEtablissement
        }) => etatAdministratifEtablissement === 'A')) {
          const {
            libelleCommuneEtablissement,
            numeroVoieEtablissement,
            typeVoieEtablissement,
            libelleVoieEtablissement,
            codePostalEtablissement,
            coordinates
          } = adresseEtablissement;
          if (coordinates) {
            const {
              latitude,
              longitude
            } = coordinates;
            res.push({
              siret,
              name: libelleCommuneEtablissement || siret,
              place: {
                coordinates: [longitude, latitude],
                primaryText: [numeroVoieEtablissement, typeVoieEtablissement, libelleVoieEtablissement].filter(Boolean).join(' '),
                secondaryText: [codePostalEtablissement, libelleCommuneEtablissement].filter(Boolean).join(' ')
              },
              isHeadquarters: Boolean(etablissementSiege)
            });
          }
        }
        return res;
      }, []);
    } catch (err) {
      console.error('[PartnerService][getCompanyEstablishment]', err);
      throw err;
    }
  }
  static async getCompaniesInArea({
    partner,
    period,
    unit
  }) {
    try {
      const params = [{
        key: 'period',
        value: 'custom'
      }, {
        key: 'date_start',
        value: period.from.format('DD-MM-YYYY')
      }, {
        key: 'date_end',
        value: period.to.format('DD-MM-YYYY')
      }];
      if (unit) params.push({
        key: 'unit',
        value: unit
      });
      const data = await HttpService.get('v1', `/stats/partner/${partner.id}/companies_in_area`, params, [], null, {
        partner: partner.code
      });
      const parseBase = ({
        parent_title: parentTitle,
        photo,
        group_title: title,
        group_geo_point: geoPoint,
        group_total_members: membersCount
      }) => ({
        geoPoint,
        parentTitle,
        photo: photo ? `${defaultEnvironment.backendUrl}${photo}` : null,
        membersCount,
        title
      });
      const parseStats = ({
        stats_counter_home_work_reference_trips_distance: homeWorkDistance,
        stats_counter_home_work_reference_trips_traces: homeWorkTracesCount,
        counter_active_user_with_home_work_trace: homeWorkActiveMembersCount
      }) => ({
        homeWorkActiveMembersCount,
        homeWorkDistance,
        homeWorkTracesCount
      });
      if (unit === 'month') {
        return data.map(company => ({
          ...parseBase(company),
          monthlyStats: company.stats.map(stats => ({
            month: moment(stats.month, 'YYYY-MM-DD'),
            ...parseStats(stats)
          }))
        }));
      }
      return data.map(company => ({
        ...parseBase(company),
        ...parseStats(company)
      }));
    } catch (err) {
      console.error('[PartnerService][getCompaniesInArea]', err);
      throw err;
    }
  }
  static async getCompaniesTransportHabitsInAreaStats({
    partner
  }) {
    try {
      const data = await HttpService.get('v1', `/stats/partner/${partner.id}/companies_transport_habits_in_area`, [], [], null, {
        partner: partner.code
      });
      return parseMobilitySurveyStats(data);
    } catch (err) {
      console.error('[PartnerService][getCompaniesTransportHabitsInAreaStats]', err);
      throw err;
    }
  }
  static async getEmployeesStats({
    partner
  }) {
    try {
      const {
        count_converted_employees: convertedEmployeesCount,
        count_convertible_employees: convertibleEmployeesCount
      } = await HttpService.get('v1', `/partners/${partner.id}/employee_stats`, [], [], null, {
        partner: partner.code
      });
      return {
        convertedEmployeesCount,
        convertibleEmployeesCount
      };
    } catch (err) {
      console.error('[PartnerService][getEmployeesStats]', err);
      throw err;
    }
  }
  static async addCompanyData({
    partner: {
      id: partnerId
    },
    fmdStatus,
    declaredEmployeeCount
  }) {
    try {
      const params = {
        fmd_stage: fmdStatus ? backendPartnerCompanyDataFMDStatusMap[fmdStatus] : 'UNKNOWN'
      };
      if (declaredEmployeeCount !== undefined) params.declared_employee_count = declaredEmployeeCount;
      const data = await HttpService.post('v1', `/partners/${partnerId}/company`, [], [], JSON.stringify(params));
      return parsePartnerCompanyData(data);
    } catch (err) {
      console.error('[PartnerService][addCompanyData]', err);
      throw err;
    }
  }
  static async updateCompanyData({
    partner: {
      id: partnerId
    },
    fmdStatus,
    declaredEmployeeCount
  }) {
    try {
      const params = {};
      if (fmdStatus) params.fmd_stage = backendPartnerCompanyDataFMDStatusMap[fmdStatus];
      if (declaredEmployeeCount !== undefined) params.declared_employee_count = declaredEmployeeCount;
      const data = await HttpService.patch('v1', `/partners/${partnerId}/company`, [], [], JSON.stringify(params));
      return parsePartnerCompanyData(data);
    } catch (err) {
      console.error('[PartnerService][updateCompanyData]', err);
      throw err;
    }
  }
  static async getPotentialCyclists({
    partner,
    siteId
  }) {
    try {
      const params = [];
      if (siteId) params.push({
        key: 'site_id',
        value: siteId
      });
      const data = await HttpService.get('v1', `/partners/${partner.id}/potential_cyclists`, params, [], null, {
        partner: partner.code
      });
      const item = Array.isArray(data) ? data[0] : data;
      if (!item) return null;
      return {
        group: item.group,
        updated: item.updated,
        employeeCount: item.employee_count,
        potentialCyclists: item.potential_cyclists,
        countEmployeeWithin15Minutes: item.count_employee_within_15_minutes,
        cyclabilityScore: item.cyclability_score,
        co2KgSavedTarget: item.co2_kg_saved_target
      };
    } catch {
      return null;
    }
  }
  static async savePotentialCyclists({
    partner,
    employeeCount,
    groupId,
    isUpdate
  }) {
    const body = JSON.stringify({
      employee_count: employeeCount,
      ...(groupId !== undefined && {
        group: groupId
      })
    });
    const data = await (isUpdate ? HttpService.patch('v1', `/partners/${partner.id}/potential_cyclists`, [], [], body, {
      partner: partner.code
    }) : HttpService.post('v1', `/partners/${partner.id}/potential_cyclists`, [], [], body, {
      partner: partner.code
    }));
    return {
      group: data.group,
      updated: data.updated,
      employeeCount: data.employee_count,
      potentialCyclists: data.potential_cyclists,
      countEmployeeWithin15Minutes: data.count_employee_within_15_minutes,
      cyclabilityScore: data.cyclability_score,
      co2KgSavedTarget: data.co2_kg_saved_target
    };
  }
  static async getActiveCommuterStats({
    groupId,
    partner
  }) {
    try {
      const params = [];
      if (groupId) params.push({
        key: 'group_id',
        value: groupId
      });
      const {
        count_active: countActive,
        count_regular: countRegular,
        count_new: countNew,
        count_dropped: countDropped
      } = await HttpService.get('v1', `/partners/${partner.id}/active_commuter_stats`, params, [], null, {
        partner: partner.code
      });
      return {
        countActive,
        countRegular,
        countNew,
        countDropped
      };
    } catch (err) {
      console.error('[PartnerService][getActiveCommuterStats]', err);
      throw err;
    }
  }
  static async getCompaniesInAreaActiveCommuterStats({
    partner
  }) {
    try {
      const {
        count_active: countActive,
        count_regular: countRegular,
        count_new: countNew,
        count_dropped: countDropped
      } = await HttpService.get('v1', `/stats/partner/${partner.id}/companies_in_area/active_commuter_stats`, [], [], null, {
        partner: partner.code
      });
      return {
        countActive,
        countRegular,
        countNew,
        countDropped
      };
    } catch (err) {
      console.error('[PartnerService][getCompaniesInAreaActiveCommuterStats]', err);
      throw err;
    }
  }
  static async getNewCommuterTransportModeStats({
    partner,
    startDate,
    endDate
  }) {
    try {
      const params = [{
        key: 'start_date',
        value: startDate.format('YYYY-MM-DD')
      }, {
        key: 'end_date',
        value: endDate.format('YYYY-MM-DD')
      }];
      const {
        employee_new_commuters_count: employeeNewCommutersCount,
        respondent_count: respondentCount,
        count_transport_modes: countTransportModes
      } = await HttpService.get('v1', `/partners/${partner.id}/new_commuter_transport_mode_stats`, params, [], null, {
        partner: partner.code
      });
      return {
        countTransportModes,
        employeeNewCommutersCount,
        respondentCount
      };
    } catch (err) {
      console.error('[PartnerService][getNewCommuterTransportModeStats]', err);
      throw err;
    }
  }
  static async getTransportHabits({
    partner,
    page,
    pageSize,
    ordering,
    search,
    filters,
    subGroupId
  }) {
    try {
      const params = [];
      if (page) params.push({
        key: 'page',
        value: page
      });
      if (pageSize) params.push({
        key: 'page_size',
        value: pageSize
      });
      if (ordering) params.push({
        key: 'ordering',
        value: ordering
      });
      if (search) params.push({
        key: 'search',
        value: search
      });
      if (subGroupId) params.push({
        key: 'sub_group_id',
        value: subGroupId
      });
      if (filters?.transportModes?.length) {
        params.push({
          key: 'transportation_modes',
          value: filters.transportModes.map(mode => transportModesMap[mode]).join(',')
        });
      }
      if (filters?.vaeTiers?.length) {
        const tiers = filters.vaeTiers;
        if (tiers.includes('lt15')) params.push({
          key: 'max_bike_itinerary_duration_in_seconds',
          value: 900
        });else if (tiers.includes('15to30')) {
          params.push({
            key: 'min_bike_itinerary_duration_in_seconds',
            value: 900
          });
          params.push({
            key: 'max_bike_itinerary_duration_in_seconds',
            value: 1800
          });
        } else if (tiers.includes('30to45')) {
          params.push({
            key: 'min_bike_itinerary_duration_in_seconds',
            value: 1800
          });
          params.push({
            key: 'max_bike_itinerary_duration_in_seconds',
            value: 2700
          });
        } else if (tiers.includes('gt45')) params.push({
          key: 'min_bike_itinerary_duration_in_seconds',
          value: 2700
        });
      }
      if (filters?.demotivationReasons?.length) {
        params.push({
          key: 'cycling_disadvantages',
          value: filters.demotivationReasons.map(reason => demotivationReasonsMap[reason]).join(',')
        });
      }
      if (filters?.bikeReasons?.length) {
        params.push({
          key: 'cycling_advantages',
          value: filters.bikeReasons.map(reason => bikeReasonsMap[reason]).join(',')
        });
      }
      if (filters?.activityTags?.length) {
        const tagConditions = {
          new: {
            new: true
          },
          regular: {
            regular: true,
            new: false
          },
          occasional: {
            active: true,
            regular: false,
            new: false,
            dropped: false
          },
          dropped: {
            dropped: true,
            new: false
          },
          inactive: {
            active: false,
            new: false,
            regular: false,
            dropped: false
          }
        };
        const selected = filters.activityTags;
        const allConditions = selected.map(tag => tagConditions[tag]);
        const keys = [...new Set(allConditions.flatMap(c => Object.keys(c)))];
        keys.forEach(key => {
          const values = allConditions.filter(c => key in c).map(c => c[key]);
          const allAgree = values.every(v => v === values[0]);
          if (allAgree) params.push({
            key,
            value: values[0]
          });
        });
      }
      const {
        count,
        next,
        results
      } = await HttpService.get('v1', `/partners/${partner.id}/transport_habits`, params, [], null, {
        partner: partner.code
      });
      return {
        count,
        hasNext: Boolean(next),
        results: results.map(parsePartnerTransportHabitsItem)
      };
    } catch (err) {
      console.error('[PartnerService][getTransportHabits]', err);
      throw err;
    }
  }
  static async getRepresentativeness({
    partner,
    limit
  }) {
    try {
      const params = [];
      if (limit !== undefined) params.push({
        key: 'limit',
        value: limit
      });
      const results = await HttpService.get('v1', `/partners/${partner.id}/representativeness`, params, [], null, {
        partner: partner.code
      });
      return results.map(parsePartnerRepresentativeness);
    } catch (err) {
      console.error('[PartnerService][getRepresentativeness]', err);
      throw err;
    }
  }
}
function parsePartnerTransportHabitsItem({
  id,
  active,
  regular,
  new: isNew,
  dropped,
  goes_to_work_by_bicycle,
  transportation_modes,
  alternative_to_bike_transportation_modes,
  cycling_advantages,
  cycling_disadvantages,
  cycling_disadvantage_other: cyclingDisadvantageOther,
  ready_to_try_cycling,
  commute_duration_in_minutes: maxDurationInMinutes,
  average_commute_count_by_week: averageCommuteCountByWeek,
  bike_itinerary_duration_in_seconds: bikeItineraryDurationInSeconds,
  bike_itinerary_distance_in_meters: bikeItineraryDistanceInMeters,
  estimated_emitted_co2_kg_per_year: estimatedEmittedCO2KgPerYear,
  work_site_id: workSiteId,
  employee
}) {
  return {
    id,
    active: active ?? false,
    regular: regular ?? false,
    isNew: isNew ?? false,
    dropped: dropped ?? false,
    goesToWorkByBicycle: goes_to_work_by_bicycle ?? false,
    transportModes: (transportation_modes ?? []).map(key => backendMobilitySurveyTransportModes[key]).filter(Boolean),
    alternativeTransportModes: (alternative_to_bike_transportation_modes ?? []).map(key => backendMobilitySurveyTransportModes[key]).filter(Boolean),
    bikeReasons: (cycling_advantages ?? []).map(key => backendBikeReasonsMap[key]).filter(Boolean),
    demotivationReasons: (cycling_disadvantages ?? []).map(key => backendDemotivationReasonsMap[key]).filter(Boolean),
    cyclingDisadvantageOther: cyclingDisadvantageOther ?? null,
    readyToTryCycling: ready_to_try_cycling ?? false,
    maxDurationInMinutes,
    averageCommuteCountByWeek,
    bikeItineraryDurationInSeconds,
    bikeItineraryDistanceInMeters,
    estimatedEmittedCO2KgPerYear,
    workSiteId,
    employee: employee ? {
      id: employee.id,
      firstName: employee.first_name,
      lastName: employee.last_name,
      professionalEmail: employee.professional_email,
      profilePicture: employee.user?.profile_picture ? `${defaultEnvironment.backendUrl}${employee.user.profile_picture}` : null,
      username: employee.user?.username
    } : null
  };
}
function parsePermission(key, extrapolated) {
  if (extrapolated) return 'extrapolated';
  if (key === 'Write') return 'write';
  if (key === 'Read') return 'read';
  return 'none';
}
export function parsePartner({
  is_admin_partner,
  is_default,
  partner: {
    id,
    area,
    cyclability_zone,
    code,
    title,
    icon,
    geo_group,
    bounds: _bounds,
    administrative_level,
    count_eco_counter,
    contact_email,
    created,
    whitelisted_domains,
    contracts,
    siren_number,
    has_stripe_client,
    company
  },
  permitted_pages
}, isAdmin, isSupport) {
  if (!code || !title) return null;
  let bounds;
  if (_bounds) {
    const [west, south, east, north] = _bounds;
    bounds = {
      north,
      east,
      south,
      west
    };
  } else {
    bounds = {
      north: 51.268318,
      east: 9.8678344,
      south: 41.2632185,
      west: -5.4534286
    };
  }
  let enableDashboardPageAccess;
  let dashboardTabsPermissions;
  if (permitted_pages) {
    enableDashboardPageAccess = {
      Agent: true,
      Api: permitted_pages.includes(Permissions.API) || permitted_pages.includes(Permissions.API_CREATE_API_KEY),
      BicycleObservatory: permitted_pages.includes(Permissions.ROAD_USE) || permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED) || permitted_pages.includes(Permissions.BICYCLE_TRAFFIC_FLUIDITY) || permitted_pages.includes(Permissions.ORIGIN_DESTINATION) || permitted_pages.includes(Permissions.GEOVELO_ACTIVITY) || permitted_pages.includes(Permissions.SUDDEN_BRAKINGS) || permitted_pages.includes(Permissions.TRAVEL_SIMULATION),
      CartographicData: permitted_pages.includes(Permissions.ACCIDENTALITY) || permitted_pages.includes(Permissions.BICYCLE_FACILITIES) || permitted_pages.includes(Permissions.NETWORK_CYCLABILITY) || permitted_pages.includes(Permissions.REPORTS) || permitted_pages.includes(Permissions.ISOCHRONES) || permitted_pages.includes(Permissions.DISCONTINUITY) || permitted_pages.includes(Permissions.ROADS_QUALITY) || (UserService.currentUser?.isGeovelo || dashboardTabsStatus.structuringNetworksImport !== 'alpha' || dashboardTabsStatus.structuringNetworksAnalysis !== 'alpha') && permitted_pages.includes(Permissions.STRUCTURING_NETWORK),
      Documents: permitted_pages.includes(Permissions.DOWNLOADS) || permitted_pages.includes(Permissions.SIG_FILES),
      Help: true,
      Home: geo_group && (permitted_pages.includes(Permissions.HOMEPAGE) || permitted_pages.includes(Permissions.HOMEPAGE_EXTRAPOLATED) || permitted_pages.includes(Permissions.HOMEPAGE_ESSENTIELLE)) || false,
      News: true,
      Parkings: permitted_pages.includes(Permissions.PARKINGS),
      Planification: (UserService.currentUser?.isGeovelo || dashboardTabsStatus.ruralRoadsReclassification !== 'alpha') && permitted_pages.includes(Permissions.RURAL_ROADS_RECLASSIFICATION),
      Settings: true,
      Promotion: permitted_pages.includes(Permissions.ANIMATION) || permitted_pages.includes(Permissions.WIDGETS) || permitted_pages.includes(Permissions.RIDES),
      QA: (code === 'geovelo' || code === 'maintenance') && (permitted_pages.includes(Permissions.OSM_REPORTS) || permitted_pages.includes(Permissions.ITINERARY_REPORTS) || permitted_pages.includes(Permissions.REFERENCE_ROUTES) || permitted_pages.includes(Permissions.EXCLUSION_AREAS) || permitted_pages.includes(Permissions.USERS_LOGS) || permitted_pages.includes(Permissions.BIKE_ROUTES) || isSupport) || false
    };
    const matches = [{
      tab: 'apiAvailability',
      backendPermission: permitted_pages.includes(Permissions.API_CREATE_API_KEY) ? 'Write' : permitted_pages.includes(Permissions.API) ? 'Read' : 'None'
    }, {
      tab: 'apiDoc',
      backendPermission: permitted_pages.includes(Permissions.API_CREATE_API_KEY) ? 'Write' : permitted_pages.includes(Permissions.API) ? 'Read' : 'None'
    }, {
      tab: 'apiKeys',
      backendPermission: permitted_pages.includes(Permissions.API_CREATE_API_KEY) || isAdmin ? 'Write' : permitted_pages.includes(Permissions.API) ? 'Read' : 'None'
    }, {
      tab: 'animation',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityChallenges',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityChallengesForm',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityMembers',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityNews',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityNewsForm',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityCompaniesMonitoring',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityCompaniesMonitoringDashboard',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityHomeWorkJourneys',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityHomeWorkJourneysCompanies',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityHomeWorkJourneysReport',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'communityHomeWorkJourneysTransportHabits',
      backendPermission: permitted_pages.includes(Permissions.ANIMATION) ? 'Write' : 'None'
    }, {
      tab: 'cyclability',
      backendPermission: permitted_pages.includes(Permissions.NETWORK_CYCLABILITY) ? 'Write' : 'None'
    }, {
      tab: 'documentsExports',
      backendPermission: permitted_pages.includes(Permissions.DOWNLOADS) ? 'Write' : 'None'
    }, {
      tab: 'documentsMapIntegrations',
      backendPermission: permitted_pages.includes(Permissions.SIG_FILES) ? 'Write' : 'None'
    }, {
      tab: 'documentsTerritorialAnalysis',
      backendPermission: permitted_pages.includes(Permissions.SIG_FILES) ? 'Write' : 'None'
    }, {
      tab: 'documentsSend',
      backendPermission: permitted_pages.includes(Permissions.SIG_FILES) ? 'Write' : 'None'
    }, {
      tab: 'facilities',
      backendPermission: permitted_pages.includes(Permissions.BICYCLE_FACILITIES) ? 'Write' : 'None'
    }, {
      tab: 'facilitiesDiffs',
      backendPermission: permitted_pages.includes(Permissions.BICYCLE_FACILITIES) ? 'Write' : 'None'
    }, {
      tab: 'facilitiesReports',
      backendPermission: permitted_pages.includes(Permissions.BICYCLE_FACILITIES) ? 'Write' : 'None'
    }, {
      tab: 'facilitiesStats',
      backendPermission: permitted_pages.includes(Permissions.BICYCLE_FACILITIES) ? 'Write' : 'None'
    }, {
      tab: 'newReport',
      backendPermission: permitted_pages.includes(Permissions.REPORTS) || permitted_pages.includes(Permissions.OSM_REPORTS) ? 'Write' : 'None'
    }, {
      tab: 'facilitiesIsochrones',
      backendPermission: permitted_pages.includes(Permissions.ISOCHRONES) ? 'Write' : 'None'
    }, {
      tab: 'facilitiesRoadsQuality',
      backendPermission: permitted_pages.includes(Permissions.ROADS_QUALITY) ? 'Write' : 'None'
    }, {
      tab: 'facilitiesRoadsQualityAnalysis',
      backendPermission: permitted_pages.includes(Permissions.ROADS_QUALITY) ? 'Write' : 'None'
    }, {
      tab: 'home',
      backendPermission: permitted_pages.includes(Permissions.HOMEPAGE) || permitted_pages.includes(Permissions.HOMEPAGE_ESSENTIELLE) || permitted_pages.includes(Permissions.HOMEPAGE_EXTRAPOLATED) ? 'Write' : 'None',
      extrapolated: permitted_pages.includes(Permissions.HOMEPAGE_EXTRAPOLATED)
    }, {
      tab: 'itineraryReports',
      backendPermission: permitted_pages.includes(Permissions.ITINERARY_REPORTS) ? 'Write' : 'None'
    }, {
      tab: 'manageContracts',
      backendPermission: permitted_pages.includes(Permissions.CONTRACTS_MANAGEMENT) ? 'Write' : 'None'
    }, {
      tab: 'manageUsers',
      backendPermission: permitted_pages.includes(Permissions.ACCESS_MANAGEMENT) ? 'Write' : 'None'
    }, {
      tab: 'osmReports',
      backendPermission: permitted_pages.includes(Permissions.OSM_REPORTS) ? 'Write' : 'None'
    }, {
      tab: 'qaRefRoutes',
      backendPermission: permitted_pages.includes(Permissions.REFERENCE_ROUTES) ? 'Write' : 'None',
      partnerCode: 'geovelo'
    }, {
      tab: 'qaUsersLogs',
      backendPermission: permitted_pages.includes(Permissions.USERS_LOGS) ? 'Write' : 'None',
      partnerCode: 'geovelo'
    }, {
      tab: 'exclusionZones',
      backendPermission: permitted_pages.includes(Permissions.EXCLUSION_AREAS) ? 'Write' : 'None',
      partnerCode: 'geovelo'
    }, {
      tab: 'exclusionZonesForm',
      backendPermission: permitted_pages.includes(Permissions.EXCLUSION_AREAS) ? 'Write' : 'None',
      partnerCode: 'geovelo'
    }, {
      tab: 'facilitiesSuggestions',
      backendPermission: permitted_pages.includes(Permissions.FACILITIES_SUGGESTIONS) ? 'Write' : 'None'
    }, {
      tab: 'facilitiesSuggestionsForm',
      backendPermission: permitted_pages.includes(Permissions.FACILITIES_SUGGESTIONS) ? 'Write' : 'None'
    }, {
      tab: 'discontinuity',
      backendPermission: permitted_pages.includes(Permissions.DISCONTINUITY) ? 'Write' : 'None'
    }, {
      tab: 'discontinuityAnalysis',
      backendPermission: permitted_pages.includes(Permissions.DISCONTINUITY) ? 'Write' : 'None'
    }, {
      tab: 'structuringNetworksImport',
      backendPermission: permitted_pages.includes(Permissions.STRUCTURING_NETWORK) ? 'Write' : 'None'
    }, {
      tab: 'structuringNetworksAnalysis',
      backendPermission: permitted_pages.includes(Permissions.STRUCTURING_NETWORK) ? 'Write' : 'None'
    }, {
      tab: 'ruralRoadsReclassification',
      backendPermission: permitted_pages.includes(Permissions.RURAL_ROADS_RECLASSIFICATION) ? 'Write' : 'None'
    }, {
      tab: 'reports',
      backendPermission: permitted_pages.includes(Permissions.REPORTS) ? 'Write' : 'None'
    }, {
      tab: 'potholeReports',
      backendPermission: permitted_pages.includes(Permissions.ROADS_QUALITY) ? 'Write' : 'None'
    }, {
      tab: 'tourismBikeRoutes',
      backendPermission: permitted_pages.includes(Permissions.BIKE_ROUTES) ? 'Write' : 'None',
      partnerCode: 'geovelo'
    }, {
      tab: 'tourismRides',
      backendPermission: permitted_pages.includes(Permissions.RIDES) ? 'Write' : 'None'
    }, {
      tab: 'parkings',
      backendPermission: permitted_pages.includes(Permissions.PARKINGS) ? 'Write' : 'None'
    }, {
      tab: 'parkingsDiffs',
      backendPermission: permitted_pages.includes(Permissions.PARKINGS) ? 'Write' : 'None'
    }, {
      tab: 'parkingsStats',
      backendPermission: permitted_pages.includes(Permissions.PARKINGS) ? 'Write' : 'None'
    }, {
      tab: 'parkingsReports',
      backendPermission: permitted_pages.includes(Permissions.PARKINGS) ? 'Write' : 'None'
    }, {
      tab: 'usageAccidentology',
      backendPermission: permitted_pages.includes(Permissions.ACCIDENTALITY) || permitted_pages.includes(Permissions.ACCIDENTALITY_EXTRAPOLATED) ? 'Write' : 'None',
      extrapolated: permitted_pages.includes(Permissions.ACCIDENTALITY_EXTRAPOLATED)
    }, {
      tab: 'usageAccidentologyAccidentZones',
      backendPermission: permitted_pages.includes(Permissions.ACCIDENTALITY) || permitted_pages.includes(Permissions.ACCIDENTALITY_EXTRAPOLATED) ? 'Write' : 'None',
      extrapolated: permitted_pages.includes(Permissions.ACCIDENTALITY_EXTRAPOLATED)
    }, {
      tab: 'usageActivityStatisticsOld',
      backendPermission: permitted_pages.includes(Permissions.GEOVELO_ACTIVITY) ? 'Write' : 'None'
    }, {
      tab: 'usageActivityStatistics',
      backendPermission: permitted_pages.includes(Permissions.GEOVELO_ACTIVITY) ? 'Write' : 'None'
    }, {
      tab: 'usageFluidity',
      backendPermission: permitted_pages.includes(Permissions.BICYCLE_TRAFFIC_FLUIDITY) ? 'Write' : 'None'
    }, {
      tab: 'usageFluidityAnalysis',
      backendPermission: permitted_pages.includes(Permissions.BICYCLE_TRAFFIC_FLUIDITY) ? 'Write' : 'None'
    }, {
      tab: 'usageOriginDestination',
      backendPermission: permitted_pages.includes(Permissions.ORIGIN_DESTINATION) ? 'Write' : 'None'
    }, {
      tab: 'usageOriginDestinationList',
      backendPermission: permitted_pages.includes(Permissions.ORIGIN_DESTINATION) ? 'Write' : 'None'
    }, {
      tab: 'usagePointAttendance',
      backendPermission: permitted_pages.includes(Permissions.ROAD_USE) || permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED) || permitted_pages.includes(Permissions.PHYSICAL_COUNTER) ? 'Write' : 'None'
    }, {
      tab: 'physicalCounters',
      backendPermission: permitted_pages.includes(Permissions.PHYSICAL_COUNTER) ? 'Write' : 'None'
    }, {
      tab: 'virtualCounterForm',
      backendPermission: permitted_pages.includes(Permissions.ROAD_USE) || permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED) ? 'Write' : 'None'
    }, {
      tab: 'usageRoadsUse',
      backendPermission: permitted_pages.includes(Permissions.ROAD_USE) || permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED) ? 'Write' : 'None',
      extrapolated: permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED)
    }, {
      tab: 'extrapolation',
      backendPermission: permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED) ? 'Write' : 'None'
    }, {
      tab: 'usageRoadsUseAnalysis',
      backendPermission: permitted_pages.includes(Permissions.ROAD_USE) || permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED) ? 'Write' : 'None',
      extrapolated: permitted_pages.includes(Permissions.ROAD_USE_EXTRAPOLATED)
    }, {
      tab: 'usageTravelSimulations',
      backendPermission: permitted_pages.includes(Permissions.TRAVEL_SIMULATION) ? 'Write' : 'None',
      partnerCode: 'lyon'
    }, {
      tab: 'widgets',
      backendPermission: permitted_pages.includes(Permissions.WIDGETS) ? 'Write' : 'None'
    }];
    dashboardTabsPermissions = matches.reduce((res, {
      tab,
      backendPermission,
      partnerCode,
      extrapolated
    }) => {
      res[tab] = partnerCode && partnerCode !== code || dashboardTabsStatus[tab] === 'alpha' && !isAdmin || dashboardTabsStatus[tab] === 'support' && !isSupport ? 'none' : parsePermission(backendPermission, extrapolated);
      return res;
    }, {
      animation: 'none',
      apiAvailability: 'none',
      apiDoc: 'none',
      apiKeys: 'none',
      communityChallenges: 'none',
      communityChallengesForm: 'none',
      communityCompaniesMonitoring: 'none',
      communityCompaniesMonitoringDashboard: 'none',
      communityHomeWorkJourneys: 'none',
      communityHomeWorkJourneysCompanies: 'none',
      communityHomeWorkJourneysReport: 'none',
      communityHomeWorkJourneysTransportHabits: 'none',
      communityMembers: 'none',
      communityNews: 'none',
      communityNewsForm: 'none',
      cyclability: 'none',
      documentsExports: 'none',
      documentsSend: 'none',
      documentsMapIntegrations: 'none',
      documentsTerritorialAnalysis: 'none',
      facilities: 'none',
      facilitiesDiffs: 'none',
      facilitiesReports: 'none',
      facilitiesStats: 'none',
      facilitiesIsochrones: 'none',
      facilitiesRoadsQuality: 'none',
      facilitiesRoadsQualityAnalysis: 'none',
      newReport: 'none',
      home: 'none',
      itineraryReports: 'none',
      manageContracts: 'none',
      manageUsers: 'none',
      osmReports: 'none',
      parkings: 'none',
      parkingsDiffs: 'none',
      parkingsStats: 'none',
      parkingsReports: 'none',
      qaRefRoutes: 'none',
      qaUsersLogs: 'none',
      exclusionZones: 'none',
      exclusionZonesForm: 'none',
      facilitiesSuggestions: 'none',
      facilitiesSuggestionsForm: 'none',
      reports: 'none',
      potholeReports: 'none',
      tourismBikeRoutes: 'none',
      tourismRides: 'none',
      usageAccidentology: 'none',
      usageAccidentologyAccidentZones: 'none',
      usageActivityStatisticsOld: 'none',
      usageActivityStatistics: 'none',
      usageFluidity: 'none',
      usageFluidityAnalysis: 'none',
      usageOriginDestination: 'none',
      usageOriginDestinationList: 'none',
      usagePointAttendance: 'none',
      physicalCounters: 'none',
      virtualCounterForm: 'none',
      usageRoadsUse: 'none',
      usageRoadsUseAnalysis: 'none',
      usageTravelSimulations: 'none',
      widgets: 'none',
      extrapolation: 'none',
      discontinuity: 'none',
      discontinuityAnalysis: 'none',
      structuringNetworksImport: 'none',
      structuringNetworksAnalysis: 'none',
      ruralRoadsReclassification: 'none'
    });
  }
  return new Partner(id, area || null, cyclability_zone || null, code, title, icon ? icon.indexOf('http') === -1 ? `${defaultEnvironment.backendUrl}${icon}` : `${icon}` : null, geo_group, bounds || null, administrativeLevels[administrative_level || 'EPCI'] || (code === 'geovelo' ? 'country' : code === 'normandie' ? 'region' : 'epci'), !!is_default, enableDashboardPageAccess || {
    Agent: false,
    Api: false,
    BicycleObservatory: false,
    CartographicData: false,
    Documents: true,
    Help: true,
    Home: false,
    News: false,
    Parkings: false,
    Settings: true,
    Promotion: false,
    QA: false,
    Planification: false
  }, dashboardTabsPermissions || {}, Boolean(count_eco_counter && count_eco_counter > 0), contact_email || null, is_admin_partner, created ? moment(created) : undefined, whitelisted_domains, contracts?.map(({
    id,
    contract_template,
    contract_template_code,
    start_datetime,
    end_datetime,
    company_workforce_count_limit,
    stripe_invoices,
    stripe_subscription_id,
    automatically_renew
  }) => ({
    id,
    contractTemplate: {
      code: contract_template_code || (typeof contract_template === 'object' ? contract_template.code : '')
    },
    contractTemplateId: typeof contract_template === 'number' ? contract_template : undefined,
    startDateTime: start_datetime ? moment(start_datetime) : null,
    endDateTime: end_datetime ? moment(end_datetime).endOf('day') : null,
    companyMaxSize: company_workforce_count_limit || null,
    stripeInvoices: stripe_invoices?.map(parseInvoice) || [],
    stripeSubscriptionId: stripe_subscription_id || null,
    automaticallyRenew: automatically_renew
  })), siren_number || null, has_stripe_client || false, company ? parsePartnerCompanyData(company) : null);
}
function parsePartnerLayer({
  id,
  name,
  geojson_content
}) {
  const collection = JSON.parse(geojson_content);
  let LegendIcon;
  if (HttpService.partner === 'larochelle') {
    collection.features.forEach(({
      properties
    }) => {
      properties.id = id;
      properties.color = '#2f81c2';
      properties.width = 3;
      LegendIcon = LaRochelleFutureFacilitiesLegendIcon;
    });
  } else if (HttpService.partner === 'lav') {
    collection.features.forEach(({
      properties
    }) => {
      properties.id = id;
      properties.width = 5;
      if (properties.amenag) {
        properties.color = '#659F4A';
        LegendIcon = LAVDedicatedRoadsLegendIcon;
      } else if (properties.shuttle) {
        properties.color = '#e6701a';
        properties.dashed = true;
        LegendIcon = LAVShuttlesLegendIcon;
      } else {
        properties.color = '#e6701a';
        LegendIcon = LAVSharedRoadsLegendIcon;
      }
    });
  }
  return {
    id,
    name,
    collection,
    LegendIcon
  };
}
function parsePartnerUser({
  user: {
    id,
    created,
    username,
    email,
    last_activity
  },
  is_admin_partner,
  last_partner_access,
  permissions
}) {
  return new PartnerUser(id, moment(created), email, username, moment(last_activity), is_admin_partner, moment(last_partner_access ? last_partner_access : created), permissions);
}
function parsePartnerGroupMemberWithoutEmployee({
  user: {
    username
  },
  user_type,
  join_date,
  last_activity_date
}) {
  return new PartnerGroupMemberWithoutEmployee(username, user_type, join_date ? moment(join_date) : null, last_activity_date ? moment(last_activity_date) : null);
}
export const partnerTypes = ['free', 'standard', 'premium'];
export const partnerTypesWithHolding = [...partnerTypes, 'holding'];
export const contractTemplateCodes = {
  'geovelo-entreprise-free': 'free',
  'geovelo-entreprise-standard': 'standard',
  'geovelo-entreprise-premium': 'premium',
  'geovelo-entreprise-holding': 'holding'
};
function parsePartnerContractTemplate({
  id,
  code,
  title,
  api_usage_limit,
  permitted_page_codes
}) {
  return new PartnerContractTemplate(id, code && contractTemplateCodes[code] || code, title, api_usage_limit, permitted_page_codes);
}
function parseCustomerInfos({
  id,
  company_name,
  company_address,
  company_country_code,
  contact_name,
  contact_email,
  siren_number,
  tva_number
}) {
  return {
    id,
    companyName: company_name || '',
    companyAddress: company_address || '',
    countryCode: company_country_code || '',
    contactName: contact_name || '',
    contactEmail: contact_email || '',
    sirenNumber: siren_number || '',
    tvaNumber: tva_number || ''
  };
}
export function parsePartnerContract({
  id,
  title,
  start_datetime,
  end_datetime,
  api_usage_limit,
  contract_template,
  company_workforce_count_limit,
  stripe_invoices,
  stripe_subscription_id,
  automatically_renew
}) {
  return new PartnerContract(id, title, moment(start_datetime), end_datetime ? moment(end_datetime).set({
    hour: 23,
    minute: 59,
    second: 59
  }) : null, api_usage_limit, typeof contract_template === 'number' ? contract_template : -1, company_workforce_count_limit || null, stripe_invoices?.map(parseInvoice) || [], stripe_subscription_id || null, automatically_renew);
}
function parsePartnerFlowsStats({
  mean_stop_time_by_kilometer: meanStopTimeByKilometer,
  distance_percent_on_greenway,
  distance_percent_on_cycleway,
  distance_percent_on_lane,
  distance_percent_on_sharebusway,
  distance_percent_on_opposite,
  distance_percent_on_mixedfacilities,
  distance_percent_on_no_facility
}) {
  return {
    meanStopTimeByKilometer,
    roadsUsedPercentages: {
      safe: distance_percent_on_cycleway + distance_percent_on_greenway,
      withoutFacilities: distance_percent_on_no_facility,
      others: distance_percent_on_lane + distance_percent_on_sharebusway + distance_percent_on_opposite + distance_percent_on_mixedfacilities
    },
    facilitiesUsedPercentages: {
      greenways: distance_percent_on_greenway,
      cycleways: distance_percent_on_cycleway,
      lanes: distance_percent_on_lane,
      mixedfacilities: distance_percent_on_mixedfacilities,
      opposites: distance_percent_on_opposite,
      sharedBusways: distance_percent_on_sharebusway
    }
  };
}
export const certificateInvalidationReasons = ['journeysOnWorkingFromHomeDay', 'journeysOnNotWorkingDay', 'other'];
const backendCertificateInvalidationReasonsMap = {
  journeysOnNotWorkingDay: 'ATTESTATION_INVALID_JOURNEYS_ON_NOT_WORKING_DAY',
  journeysOnWorkingFromHomeDay: 'ATTESTATION_INVALID_JOURNEYS_ON_WORKING_FROM_HOME_DAY',
  other: 'ATTESTATION_INVALID_OTHER'
};
const backendPartnerCompanyDataFMDStatusMap = {
  notProposed: 'FMD_IS_NOT_PLANNED',
  notProposedButMaybeOneDay: 'FMD_IS_PLANNED',
  proposed: 'FMD_IS_IMPLEMENTED',
  unknown: 'UNKNOWN'
};
function parsePartnerCompanyData({
  fmd_stage,
  declared_employee_count,
  allow_set_company_employee_id
}) {
  return {
    fmdStatus: fmd_stage === 'FMD_IS_IMPLEMENTED' ? 'proposed' : fmd_stage === 'FMD_IS_NOT_PLANNED' ? 'notProposed' : fmd_stage === 'FMD_IS_PLANNED' ? 'notProposedButMaybeOneDay' : fmd_stage === 'UNKNOWN' ? 'unknown' : null,
    declaredEmployeeCount: declared_employee_count ?? null,
    allowSetCompanyEmployeeId: allow_set_company_employee_id
  };
}
export function parseInvoice({
  invoice_id: id,
  custom_price_value_in_cents: customPriceValueInCents,
  invoice_status,
  stripe_product_id: stripeProductId,
  stripe_product_price_id: stripeProductPriceId,
  invoice_stripe_hosted_url: url,
  due_date,
  updated
}) {
  return {
    id,
    status: invoice_status === 'draft' ? 'draft' : invoice_status === 'open' ? 'open' : invoice_status === 'paid' ? 'paid' : 'cancelled',
    customPriceValueInCents,
    stripeProductId,
    stripeProductPriceId,
    url,
    date: new Date(updated),
    stripeInvoiceDueDate: due_date ? new Date(due_date) : null
  };
}
export function parsePartnerRepresentativeness({
  id,
  partner_id: partnerId,
  start_date,
  end_date,
  computed,
  geovelo_active_users: geoveloActiveUsers,
  geovelo_active_users_grade,
  geovelo_active_commuters: geoveloActiveCommuters,
  geovelo_active_commuters_grade,
  commuter_count: commuterCount,
  commuter_ratio: commuterRatio,
  commuter_ratio_grade,
  active_count: activeCount,
  active_ratio: activeRatio,
  active_ratio_grade,
  covering_ratio: coveringRatio,
  covering_ratio_grade,
  facility_covering_ratio: facilityCoveringRatio,
  facility_covering_ratio_grade
}) {
  return {
    id,
    partnerId,
    startDate: moment(start_date),
    endDate: moment(end_date),
    computed: moment(computed),
    activeCount,
    activeRatio,
    commuterCount,
    commuterRatio,
    coveringRatio,
    facilityCoveringRatio,
    geoveloActiveCommuters,
    geoveloActiveUsers,
    grades: {
      geoveloActiveUsers: geovelo_active_users_grade,
      geoveloActiveCommuters: geovelo_active_commuters_grade,
      commuterRatio: commuter_ratio_grade,
      activeRatio: active_ratio_grade,
      coveringRatio: covering_ratio_grade,
      facilityCoveringRatio: facility_covering_ratio_grade
    }
  };
}